import { z } from 'zod'
import { McpScope } from './mcp-scopes.js'
import { SCHEMA_VERSION } from './version.js'

/**
 * Versioned capability registry — Part Three C3/C4, register §6.
 *
 * One registry, shared by the app, backend, MCP adapter and automated tests. Every
 * entry carries all ten C4 fields. Coverage is 100% of supported product
 * capabilities, with direct-execution and handoff coverage reported separately, and a
 * capability is never "implemented" because a tool name exists.
 *
 * The registry declares eligibility, execution mode and blockers only — it is never an
 * authorization mechanism. The server rechecks eligibility, grants, revision and
 * approval at execution (C4, C7).
 */

/** Exactly four execution modes — C3. */
export const CapabilityExecutionMode = z.enum([
  'REMOTE',
  'DEVICE_HANDOFF',
  'HUMAN_HANDOFF',
  'UNAVAILABLE',
])
export type CapabilityExecutionMode = z.infer<typeof CapabilityExecutionMode>

/** Platform identifier from the §10.1 platform matrix (`platforms.json`). */
export const CapabilityPlatform = z.string().min(1).max(64)

/** Host surface identifier (web app, native app, MCP host, …). */
export const HostSurface = z.string().min(1).max(64)

/** Human approval / reauthentication requirement — C4 field 6. */
export const CapabilityApproval = z.enum([
  'NONE',
  'HUMAN_APPROVAL',
  'REAUTHENTICATION',
  'HUMAN_APPROVAL_AND_REAUTHENTICATION',
])
export type CapabilityApproval = z.infer<typeof CapabilityApproval>

/** Free/Lifetime eligibility — C4 field 2. */
export const CapabilityEligibility = z
  .object({
    free: z.boolean(),
    lifetime: z.boolean(),
  })
  .strict()
export type CapabilityEligibility = z.infer<typeof CapabilityEligibility>

/** Trial, credit and active-request effects — C4 field 3. */
export const CapabilityEffects = z
  .object({
    trial: z.enum(['NONE', 'CONSUMES_TRIAL', 'REQUIRES_UNCONSUMED_TRIAL']),
    credit: z.enum(['NONE', 'CONSUMES_ONE_CREDIT']),
    active_request: z.enum(['NONE', 'CONSUMES_SLOT', 'EXTENDS_SLOT']),
  })
  .strict()
export type CapabilityEffects = z.infer<typeof CapabilityEffects>

/**
 * One capability registry entry — the ten C4 fields, in order:
 * 1 `capability_id` + `requirement_ids`; 2 `eligibility`; 3 `effects`;
 * 4 `execution_modes` + `platforms`; 5 `oauth_scopes` +
 * `selected_object_permission_required`; 6 `approval`; 7 `preconditions`,
 * `resulting_state`, `prohibited_side_effects`; 8 `tool_schema_ids`,
 * `host_surfaces`, `policy_version`; 9 `recovery_behaviour`; 10 `test_ids`.
 */
export const CapabilityEntry = z
  .object({
    capability_id: z.string().min(1).max(128),
    /** Linked PRD/Addendum requirement IDs. */
    requirement_ids: z.array(z.string().min(1).max(64)).min(1),
    eligibility: CapabilityEligibility,
    effects: CapabilityEffects,
    execution_modes: z.array(CapabilityExecutionMode).min(1),
    platforms: z.array(CapabilityPlatform).min(1),
    oauth_scopes: z.array(McpScope).max(32),
    selected_object_permission_required: z.boolean(),
    approval: CapabilityApproval,
    preconditions: z.array(z.string().min(1).max(200)),
    resulting_state: z.string().min(1).max(200),
    prohibited_side_effects: z.array(z.string().min(1).max(200)),
    tool_schema_ids: z.array(z.string().min(1).max(128)),
    host_surfaces: z.array(HostSurface).min(1),
    policy_version: z.number().int().positive(),
    recovery_behaviour: z.string().min(1).max(400),
    test_ids: z.array(z.string().min(1).max(64)),
  })
  .strict()
  .superRefine((entry, ctx) => {
    if (new Set(entry.execution_modes).size !== entry.execution_modes.length) {
      ctx.addIssue({
        code: 'custom',
        message: 'execution modes must be unique per capability',
        path: ['execution_modes'],
      })
    }
    if (entry.execution_modes.includes('UNAVAILABLE') && entry.execution_modes.length > 1) {
      ctx.addIssue({
        code: 'custom',
        message: 'UNAVAILABLE is exclusive: a vendored capability has no other mode',
        path: ['execution_modes'],
      })
    }
  })
export type CapabilityEntry = z.infer<typeof CapabilityEntry>

export const CapabilityRegistry = z
  .object({
    schema_version: z.literal(SCHEMA_VERSION),
    registry_version: z.number().int().positive(),
    entries: z.array(CapabilityEntry).min(1),
  })
  .strict()
  .superRefine((registry, ctx) => {
    const ids = registry.entries.map((entry) => entry.capability_id)
    if (new Set(ids).size !== ids.length) {
      ctx.addIssue({
        code: 'custom',
        message: 'capability IDs must be unique',
        path: ['entries'],
      })
    }
  })
export type CapabilityRegistry = z.infer<typeof CapabilityRegistry>

export interface CapabilityCoverageReport {
  /** Supported product capabilities handed to the check. */
  readonly supported_count: number
  readonly registry_entry_count: number
  /** Supported capabilities with no registry entry — coverage is incomplete. */
  readonly unmapped_supported_ids: readonly string[]
  /** Registry entries matching no supported capability — stale registry rows. */
  readonly unregistered_ids: readonly string[]
  /** Entries executed remotely in-process (`REMOTE`). */
  readonly direct_execution_coverage: number
  /** Entries that require a device or human handoff. */
  readonly handoff_coverage: number
  readonly unavailable_count: number
  /** True only when every supported capability is mapped (C4: 100% coverage). */
  readonly complete: boolean
}

/**
 * Coverage lint for the capability registry — C4.
 * Reports unmapped supported IDs and reports direct-execution and handoff coverage
 * separately, so a handoff-only capability never inflates direct coverage. `complete`
 * is about mapping only; test-ID completeness is asserted by the registry tests.
 */
export function capabilityCoverage(
  registry: CapabilityRegistry,
  supportedCapabilityIds: readonly string[],
): CapabilityCoverageReport {
  const registeredIds = new Set(registry.entries.map((entry) => entry.capability_id))
  const supportedIds = new Set(supportedCapabilityIds)
  const direct = registry.entries.filter((entry) => entry.execution_modes.includes('REMOTE')).length
  const handoff = registry.entries.filter(
    (entry) =>
      entry.execution_modes.includes('DEVICE_HANDOFF') ||
      entry.execution_modes.includes('HUMAN_HANDOFF'),
  ).length
  const unavailable = registry.entries.filter((entry) =>
    entry.execution_modes.includes('UNAVAILABLE'),
  ).length
  const unmappedSupported = [...supportedIds].filter((id) => !registeredIds.has(id)).sort()
  const unregistered = [...registeredIds].filter((id) => !supportedIds.has(id)).sort()
  return {
    supported_count: supportedIds.size,
    registry_entry_count: registry.entries.length,
    unmapped_supported_ids: unmappedSupported,
    unregistered_ids: unregistered,
    direct_execution_coverage: direct,
    handoff_coverage: handoff,
    unavailable_count: unavailable,
    complete: unmappedSupported.length === 0,
  }
}
